import { CSSProperties, ReactNode } from 'react';

import { BackrsIcon } from '@backrs_ui/common-ui/src/atoms/BackrsIcon';

import { Box, IconButton, SxProps, Theme, Typography, useMediaQuery } from '@mui/material';

import { DRAWER_WIDTH_EXPANDED } from './constants';
import { IconMeta } from './types';

export type SidebarLogoProps = Readonly<{
  label: string;
  labelAsComponent?: ReactNode;
  iconMeta: IconMeta;
  isOpen: boolean;
}>;

export const SidebarLogo = ({ label, iconMeta, isOpen, labelAsComponent }: SidebarLogoProps) => {
  const disabled = typeof iconMeta?.onClick === 'function';

  const isSmallScreenHeight = useMediaQuery('(max-height: 600px)');

  return (
    <IconButton
      id="sidebar-logo-btn"
      disabled={!disabled}
      sx={iconButtonStyle}
      onClick={() => iconMeta.onClick?.()}
    >
      <Box sx={iconAndLabelStyle}>
        <BackrsIcon
          name={iconMeta.icon}
          alt={iconMeta.alt}
          style={logoStyle({ isOpen, extraStyle: iconMeta.style, isSmallScreenHeight })}
        />
        {labelAsComponent || <Typography sx={lubavTextStyle(isOpen)}>{label}</Typography>}
      </Box>
    </IconButton>
  );
};

const lubavTextStyle = (isOpen: boolean): SxProps<Theme> => ({
  fontSize: '24pt',
  color: '#03295F',
  paddingLeft: '8px',
  opacity: isOpen ? 1 : 0,
  transition: 'opacity 0.3s ease',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  display: 'block',
});

const iconAndLabelStyle: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  minHeight: '44px',
  width: '100%',
};

const logoStyle = ({
  isOpen,
  extraStyle,
  isSmallScreenHeight,
}: {
  isOpen: boolean;
  extraStyle?: CSSProperties;
  isSmallScreenHeight?: boolean;
}): CSSProperties => {
  const smallScreenIconWidth = isOpen ? '40px' : '32px';
  const largerScreenIconWidth = isOpen ? '60px' : '32px';

  const width = isSmallScreenHeight ? smallScreenIconWidth : largerScreenIconWidth;

  return {
    width,
    height: isSmallScreenHeight ? 'unset' : 'auto',
    transition: 'width 0.4s',
    ...extraStyle,
  };
};

const iconButtonStyle: SxProps<Theme> = {
  borderRadius: '16px',
  padding: '0px 4px',
  '@media (max-height: 600px)': {
    maxHeight: '40px',
  },
  width: `calc(${DRAWER_WIDTH_EXPANDED}px - 20px)`,
  transition: 'background-color 0.3s, border-radius 0.3s',
  '&:hover': { backgroundColor: 'transparent' },
  marginBottom: '12px',
  height: '70px',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
};
